import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Screen } from "@/components/screen";
import { Button, Card, DateField, Field, SelectField } from "@/components/ui";
import { useToast } from "@/contexts";
import { defaultCompetitionDraft } from "@/data/mock";
import { createCompetition } from "@/features/competitions/api";
import { getCurrentUserProfile } from "@/features/dashboard/api";
import { listOrgGroups } from "@/features/organizations/groups";
import { COMP_TYPE_LABELS, useCompetitionForm } from "@/hooks/useCompetitionForm";
import { COMPETITION_BADGE_COUNT } from "@/server/data/badges";
import type { CompetitionType, Group, ScopeMode, TeamScoring } from "@/types";

const now = new Date();
const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
const weekFromNow = new Date();
weekFromNow.setDate(weekFromNow.getDate() + 7);
const weekFromNowStr = `${weekFromNow.getFullYear()}-${String(weekFromNow.getMonth() + 1).padStart(2, "0")}-${String(weekFromNow.getDate()).padStart(2, "0")}`;

export default function CompetitionCreate() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [compType, setCompType] = useState<CompetitionType>("step_race");
  const [teamScoring, setTeamScoring] = useState<TeamScoring>("sum");
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(weekFromNowStr);
  const [targetSteps, setTargetSteps] = useState("50000");
  const [dailyGoalSteps, setDailyGoalSteps] = useState("10000");
  const [visibility, setVisibility] = useState<"invite-only" | "discoverable">(
    defaultCompetitionDraft.visibility,
  );
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [hasOrg, setHasOrg] = useState(false);
  const [scopeMode, setScopeMode] = useState<"open" | ScopeMode>("open");
  const [orgGroups, setOrgGroups] = useState<Group[]>([]);
  const [scopeGroupIds, setScopeGroupIds] = useState<string[]>([]);

  const {
    badgeMode,
    setBadgeMode,
    selectedBadgeIds,
    setSelectedBadgeIds,
    autoBadgeIds,
    toggleBadge,
    handleShuffle,
    autoBadges,
    sortedAllBadges,
  } = useCompetitionForm();

  const toast = useToast();

  useEffect(() => {
    (async () => {
      const result = await getCurrentUserProfile();
      if (result.data?.timezone) {
        setTimezone(result.data.timezone);
      }
      if (result.data?.orgId) {
        setHasOrg(true);
        const groupsResult = await listOrgGroups(result.data.orgId);
        if (groupsResult.data) {
          setOrgGroups(groupsResult.data);
        }
      }
    })();
  }, []);

  async function handleCreateCompetition() {
    if (!name.trim()) {
      toast.showAlert("Name required", "Give your competition a name before creating it.", "error");
      return;
    }
    if (compType === "target_race" && (!targetSteps || Number(targetSteps) < 1)) {
      toast.showAlert("Target required", "Set a step target for the race.", "error");
      return;
    }
    if (compType === "daily_goals" && (!dailyGoalSteps || Number(dailyGoalSteps) < 1)) {
      toast.showAlert("Goal required", "Set a daily step goal for the competition.", "error");
      return;
    }

    const badgeIds = badgeMode === "auto" ? autoBadgeIds : selectedBadgeIds;

    const { data, error } = await createCompetition({
      name: name.trim(),
      description: description.trim(),
      type: compType,
      startDate,
      endDate,
      targetSteps: compType === "target_race" ? Number(targetSteps) : null,
      dailyGoalSteps: compType === "daily_goals" ? Number(dailyGoalSteps) : null,
      ...(hasOrg ? { teamScoring } : {}),
      timezone,
      visibility: visibility === "discoverable" ? "discoverable" : "invite-only",
      badgeIds,
      ...(hasOrg && scopeMode !== "open"
        ? { scopeMode, scopeGroupIds: scopeGroupIds.length > 0 ? scopeGroupIds : undefined }
        : {}),
    });

    if (error) {
      toast.showAlert("Create failed", error.message, "error");
      return;
    }

    navigate({ to: `/competition/${data}`, replace: true });
  }

  return (
    <Screen
      backTo="/dashboard"
      eyebrow="Create flow"
      title="Create a competition"
      subtitle="Set the challenge dates, choose invite-only or discoverable, then share the invite code from the competition page."
    >
      <div className="flex flex-col gap-3.5 pb-10">
        <Card tone="strong">
          <Field label="Competition name" value={name} onChangeText={setName} />
          <Field label="Description" value={description} onChangeText={setDescription} multiline />
          <SelectField
            label="Competition type"
            value={compType}
            options={[
              { label: "Step race", value: "step_race" },
              { label: "Target race", value: "target_race" },
              { label: "Daily goals", value: "daily_goals" },
            ]}
            onValueChange={(v) => setCompType(v as CompetitionType)}
          />
          <p className="text-text-muted text-xs font-semibold -mt-2">
            {COMP_TYPE_LABELS[compType].desc}
          </p>

          {hasOrg && (
            <>
              <span className="text-text-muted text-[0.625rem] font-bold uppercase tracking-[0.05em] mt-1">
                Team scoring
              </span>
              <div
                role="radiogroup"
                aria-label="Team scoring"
                className="flex flex-row bg-[rgba(0,0,0,0.04)] rounded-2xl p-0.5 gap-0"
              >
                <label
                  className={`flex-1 py-2 text-center rounded-xl cursor-pointer font-inherit border-0 ${
                    teamScoring === "sum" ? "bg-aqua" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="team-scoring"
                    checked={teamScoring === "sum"}
                    onChange={() => setTeamScoring("sum")}
                    className="sr-only"
                  />
                  <span
                    className={`text-xs font-semibold ${teamScoring === "sum" ? "text-white" : "text-text-muted"}`}
                  >
                    Sum
                  </span>
                </label>
                <label
                  className={`flex-1 py-2 text-center rounded-xl cursor-pointer font-inherit border-0 ${
                    teamScoring === "average" ? "bg-aqua" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="team-scoring"
                    checked={teamScoring === "average"}
                    onChange={() => setTeamScoring("average")}
                    className="sr-only"
                  />
                  <span
                    className={`text-xs font-semibold ${teamScoring === "average" ? "text-white" : "text-text-muted"}`}
                  >
                    Average
                  </span>
                </label>
              </div>
              <p className="text-text-muted text-xs font-semibold -mt-1">
                {teamScoring === "average"
                  ? "Average steps per member (fair for unequal teams)"
                  : "Total steps across all members"}
              </p>
            </>
          )}

          {compType === "target_race" && (
            <Field label="Target steps" value={targetSteps} onChangeText={setTargetSteps} />
          )}
          {compType === "daily_goals" && (
            <Field
              label="Daily step goal"
              value={dailyGoalSteps}
              onChangeText={setDailyGoalSteps}
            />
          )}
          <DateField label="Start date" value={startDate} onValueChange={setStartDate} />
          <DateField label="End date" value={endDate} onValueChange={setEndDate} />
          <SelectField
            label="Visibility"
            value={visibility}
            options={[
              { label: "Invite-only", value: "invite-only" },
              { label: "Discoverable", value: "discoverable" },
            ]}
            onValueChange={(v) => setVisibility(v as "invite-only" | "discoverable")}
          />
        </Card>

        {hasOrg && (
          <Card tone="strong">
            <span className="text-text-muted text-[0.625rem] font-bold uppercase tracking-[0.05em]">
              Competition scope
            </span>
            <p className="text-text-muted text-xs mt-0.5">
              Control who can join this competition based on group membership.
            </p>

            <div
              role="radiogroup"
              aria-label="Competition scope mode"
              className="flex flex-row bg-[rgba(0,0,0,0.04)] rounded-2xl p-0.5 gap-0"
            >
              <label
                className={`flex-1 py-2 text-center rounded-xl cursor-pointer font-inherit border-0 ${
                  scopeMode === "open" ? "bg-aqua" : ""
                }`}
              >
                <input
                  type="radio"
                  name="scope-mode"
                  checked={scopeMode === "open"}
                  onChange={() => {
                    setScopeMode("open");
                    setScopeGroupIds([]);
                  }}
                  className="sr-only"
                />
                <span
                  className={`text-xs font-semibold ${scopeMode === "open" ? "text-white" : "text-text-muted"}`}
                >
                  Open
                </span>
              </label>
              <label
                className={`flex-1 py-2 text-center rounded-xl cursor-pointer font-inherit border-0 ${
                  scopeMode === "group" ? "bg-aqua" : ""
                }`}
              >
                <input
                  type="radio"
                  name="scope-mode"
                  checked={scopeMode === "group"}
                  onChange={() => setScopeMode("group")}
                  className="sr-only"
                />
                <span
                  className={`text-xs font-semibold ${scopeMode === "group" ? "text-white" : "text-text-muted"}`}
                >
                  Group only
                </span>
              </label>
              <label
                className={`flex-1 py-2 text-center rounded-xl cursor-pointer font-inherit border-0 ${
                  scopeMode === "group_cross_office" ? "bg-aqua" : ""
                }`}
              >
                <input
                  type="radio"
                  name="scope-mode"
                  checked={scopeMode === "group_cross_office"}
                  onChange={() => setScopeMode("group_cross_office")}
                  className="sr-only"
                />
                <span
                  className={`text-xs font-semibold ${scopeMode === "group_cross_office" ? "text-white" : "text-text-muted"}`}
                >
                  Cross-office
                </span>
              </label>
              <label
                className={`flex-1 py-2 text-center rounded-xl cursor-pointer font-inherit border-0 ${
                  scopeMode === "cross_group" ? "bg-aqua" : ""
                }`}
              >
                <input
                  type="radio"
                  name="scope-mode"
                  checked={scopeMode === "cross_group"}
                  onChange={() => setScopeMode("cross_group")}
                  className="sr-only"
                />
                <span
                  className={`text-xs font-semibold ${scopeMode === "cross_group" ? "text-white" : "text-text-muted"}`}
                >
                  Cross-group
                </span>
              </label>
            </div>

            {scopeMode !== "open" && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className="text-text-dim text-[0.75rem] font-bold">
                    {scopeGroupIds.length} group{scopeGroupIds.length !== 1 ? "s" : ""} selected
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setScopeGroupIds(orgGroups.map((g) => g.id))}
                      className="text-aqua text-[0.6875rem] font-bold bg-transparent border-0 cursor-pointer"
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      onClick={() => setScopeGroupIds([])}
                      className="text-text-muted text-[0.6875rem] font-bold bg-transparent border-0 cursor-pointer"
                    >
                      Deselect all
                    </button>
                  </div>
                </div>
                {orgGroups.map((g) => {
                  const isSelected = scopeGroupIds.includes(g.id);
                  return (
                    <button
                      type="button"
                      key={g.id}
                      onClick={() => {
                        setScopeGroupIds((prev) =>
                          isSelected ? prev.filter((id) => id !== g.id) : [...prev, g.id],
                        );
                      }}
                      className={`flex flex-row items-center gap-2.5 bg-white rounded-xl p-2.5 border cursor-pointer font-inherit text-left ${
                        isSelected
                          ? "border-aqua bg-[rgba(255,45,126,0.04)]"
                          : "border-[rgba(28,27,46,0.1)]"
                      }`}
                    >
                      <div
                        className={`w-[1.375rem] h-[1.375rem] rounded border-2 flex items-center justify-center shrink-0 ${
                          isSelected ? "bg-aqua border-aqua" : "border-text-muted"
                        }`}
                      >
                        {isSelected && <span className="text-white text-xs font-extrabold">✓</span>}
                      </div>
                      <div className="flex-1">
                        <span className="text-text text-xs font-semibold">{g.name}</span>
                        <span className="text-text-muted text-[0.6875rem] block">/{g.slug}</span>
                      </div>
                      <span className="text-text-dim text-[0.625rem] font-bold uppercase">
                        {g.memberCount} members
                      </span>
                    </button>
                  );
                })}
                {orgGroups.length === 0 && (
                  <p className="text-text-muted text-xs italic">
                    No groups in your organization. Create groups in Settings.
                  </p>
                )}
              </div>
            )}
          </Card>
        )}

        <Card tone="strong">
          <span className="text-text-muted text-[0.625rem] font-bold uppercase tracking-[0.05em]">
            Badges
          </span>
          <p className="text-text-muted text-xs mt-0.5">
            Choose which badges will be available in this competition.
          </p>

          <div
            role="radiogroup"
            aria-label="Badge selection mode"
            className="flex flex-row bg-[rgba(0,0,0,0.04)] rounded-2xl p-0.5 gap-0"
          >
            <label
              className={`flex-1 py-2 text-center rounded-xl cursor-pointer font-inherit border-0 ${
                badgeMode === "auto" ? "bg-aqua" : ""
              }`}
            >
              <input
                type="radio"
                name="badge-mode"
                checked={badgeMode === "auto"}
                onChange={() => setBadgeMode("auto")}
                className="sr-only"
              />
              <span
                className={`text-xs font-semibold ${badgeMode === "auto" ? "text-white" : "text-text-muted"}`}
              >
                Pick for me
              </span>
            </label>
            <label
              className={`flex-1 py-2 text-center rounded-xl cursor-pointer font-inherit border-0 ${
                badgeMode === "manual" ? "bg-aqua" : ""
              }`}
            >
              <input
                type="radio"
                name="badge-mode"
                checked={badgeMode === "manual"}
                onChange={() => setBadgeMode("manual")}
                className="sr-only"
              />
              <span
                className={`text-xs font-semibold ${badgeMode === "manual" ? "text-white" : "text-text-muted"}`}
              >
                Choose my own
              </span>
            </label>
          </div>

          {badgeMode === "auto" ? (
            <div className="flex flex-col gap-2.5">
              {autoBadges.map((badge) => (
                <div
                  key={badge.badgeId}
                  className="flex flex-row items-center gap-2.5 bg-white rounded-xl p-2.5 border border-[rgba(28,27,46,0.1)]"
                >
                  <span className="text-2xl">{badge.iconEmoji}</span>
                  <div className="flex-1">
                    <span className="text-text text-xs font-semibold">{badge.name}</span>
                    <p className="text-text-muted text-[0.6875rem] mt-0.5">
                      {badge.stepThreshold.toLocaleString()} steps — {badge.description}
                    </p>
                  </div>
                </div>
              ))}
              <Button label="Shuffle badges" kind="secondary" onPress={handleShuffle} />
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <p className="text-text-muted text-xs font-semibold text-center">
                {selectedBadgeIds.length} / {COMPETITION_BADGE_COUNT} selected
              </p>
              {sortedAllBadges.map((badge) => {
                const isSelected = selectedBadgeIds.includes(badge.badgeId);
                const isFull = selectedBadgeIds.length >= COMPETITION_BADGE_COUNT && !isSelected;
                return (
                  <button
                    type="button"
                    key={badge.badgeId}
                    onClick={() =>
                      setSelectedBadgeIds(toggleBadge(selectedBadgeIds, badge.badgeId))
                    }
                    disabled={isFull}
                    className={`flex flex-row items-center gap-2.5 bg-white rounded-xl p-2.5 border cursor-pointer font-inherit text-left ${
                      isSelected
                        ? "border-aqua bg-[rgba(255,45,126,0.04)]"
                        : "border-[rgba(28,27,46,0.1)]"
                    } ${isFull ? "opacity-40" : ""}`}
                  >
                    <div
                      className={`w-[1.375rem] h-[1.375rem] rounded border-2 flex items-center justify-center shrink-0 ${
                        isSelected ? "bg-aqua border-aqua" : "border-text-muted"
                      }`}
                    >
                      {isSelected && <span className="text-white text-xs font-extrabold">✓</span>}
                    </div>
                    <span className="text-2xl">{badge.iconEmoji}</span>
                    <div className="flex-1">
                      <span className="text-text text-xs font-semibold">{badge.name}</span>
                      <p className="text-text-muted text-[0.6875rem] mt-0.5">
                        {badge.stepThreshold.toLocaleString()} steps — {badge.description}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </Card>

        <Button
          label="Create competition"
          onPress={handleCreateCompetition}
          kind={
            badgeMode === "manual" && selectedBadgeIds.length !== COMPETITION_BADGE_COUNT
              ? "ghost"
              : "primary"
          }
        />
      </div>
    </Screen>
  );
}
